import type { ApolloQueryResult } from '@apollo/client';
import Image from 'next/image';
import React from 'react';

import { createKeyboardClickHandler } from '@/utils/accessibility';

import { Modal } from '@/components/Modal';
import type { ContentfulSelectorModalByMarketingPartnerQuery } from '@/graphql/contentful/api';
import type { ProductsByMarketingPartnerQuery } from '@/graphql/saleor/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import { useRouter } from '@/hooks/useRouter';

import * as S from './MarketingSelectorModal.styles';

// reorder items for the desktop column layout
const reorderItemsForColumns = <T,>(items: T[], columns: number): T[] => {
  if (items.length === 0) return items;

  if (items.length === 6 && columns === 3) {
    const rows = Math.ceil(items.length / columns);
    const reorderedItems: typeof items = [];

    for (let i = 0; i < items.length; i++) {
      const row = i % rows;
      const col = Math.floor(i / rows);
      const newIndex = row * columns + col;
      reorderedItems[newIndex] = items[i];
    }
    return reorderedItems;
  }

  const rows = Math.ceil(items.length / columns);
  const reorderedItems: T[] = [];

  for (let i = 0; i < items.length; i++) {
    const row = i % rows;
    const col = Math.floor(i / rows);
    const newIndex = row * columns + col;

    if (newIndex < items.length) {
      reorderedItems[newIndex] = items[i];
    }
  }
  return reorderedItems.filter((item) => item !== undefined);
};

export interface MarketingSelectorModalProps {
  close: () => void;
  isOpen: boolean;
  marketingPartnerProductsResult?: ApolloQueryResult<ProductsByMarketingPartnerQuery>;
  marketingSelectorModalResult?:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  onClose: () => void;
  scrollToTopWhenOpen?: boolean;
}

const MarketingSelectorModal: React.FC<MarketingSelectorModalProps> = ({
  close,
  isOpen,
  marketingPartnerProductsResult,
  marketingSelectorModalResult,
  onClose,
  scrollToTopWhenOpen,
}) => {
  const router = useRouter();
  const { isMobile } = useDeviceDetect();

  const products = marketingPartnerProductsResult?.data?.products?.edges || [];

  const validItems: {
    name: string | undefined;
    sku: string | undefined;
  }[] = [];

  for (const item of products) {
    const sku = item?.node?.slug;
    const attributes = item?.node?.attributes;

    const marketingCampaignAttribute = attributes?.find(
      (attr) => attr?.attribute?.slug === 'com-marketing-campaign',
    );
    const name = marketingCampaignAttribute?.values[0]?.name as
      | string
      | undefined;

    if (name) {
      validItems.push({ name, sku });
    }
  }

  // Filter items to only include those with matching products
  const allItems = marketingSelectorModalResult?.linksCollection?.items || [];
  const filteredItems =
    products.length !== 0
      ? allItems.filter((marketingItem) => {
          if (!marketingItem?.text) return false;
          return validItems.some((item) => item.name === marketingItem.text);
        })
      : allItems;

  const itemsCount = filteredItems.length;
  const isSixItemLayout = itemsCount === 6;

  const handlePDPItemLinkClick = (sku: string | null | undefined) => {
    if (sku) {
      const pathParts = router.asPath.split('/');
      pathParts[pathParts.length - 2] = sku;
      const newPath = pathParts.join('/');
      router.push(newPath);
      close();
    }
  };

  const handlePLPItemLinkClick = (slug: string | null | undefined) => {
    if (slug) {
      const pathParts = router.asPath.split('/');
      const newPath = `/${pathParts[1]}/${slug}`;
      router.push(newPath);
      close();
    }
  };

  return (
    <Modal
      alignItems='center'
      className='marketing-selector-modal'
      data-test-id='marketingSelectorModal'
      gutters={false}
      height='auto'
      isOpen={isOpen}
      maxWidth='md'
      onClose={onClose}
      scrollToTopWhenOpen={scrollToTopWhenOpen}
      width={
        filteredItems.length
          ? isSixItemLayout
            ? '800px'
            : '1000px'
          : undefined
      }
    >
      <Modal.Header
        data-test-id='marketingSelectorModalHeader'
        divider={true}
        onClose={onClose}
      >
        <h2
          style={{
            marginBottom: '0.5em',
            ...(isSixItemLayout && {
              fontSize: '24px',
              fontWeight: '600',
            }),
          }}
        >
          {marketingSelectorModalResult?.title}
        </h2>
      </Modal.Header>
      <Modal.Body data-test-id='marketingSelectorModalBody'>
        <S.OptionsContainer
          className={`marketing-item-grid ${
            isSixItemLayout ? 'six-items' : ''
          }`}
          data-test-id='marketingSelectorModalOptionsContainer'
        >
          {filteredItems.length > 0 &&
            (() => {
              const orderedItems = !isMobile
                ? reorderItemsForColumns(filteredItems, isSixItemLayout ? 3 : 4)
                : filteredItems;

              return orderedItems.map((marketingItem, index) => {
                const originalIndex = !isMobile
                  ? filteredItems.indexOf(marketingItem)
                  : index;

                if (products.length !== 0) {
                  const matchingItem = marketingItem?.text
                    ? validItems.find(
                        (item) => item.name === marketingItem.text,
                      )
                    : undefined;

                  return (
                    <S.OptionBox
                      key={marketingItem?.reference || originalIndex}
                      aria-disabled={!matchingItem?.sku}
                      aria-label={marketingItem?.text ?? ''}
                      className='marketing-item-selector'
                      data-test-id={`marketingSelectorModalOption-${originalIndex}`}
                      onClick={() =>
                        matchingItem?.sku &&
                        handlePDPItemLinkClick(matchingItem.sku)
                      }
                      onKeyDown={createKeyboardClickHandler(
                        () =>
                          matchingItem?.sku &&
                          handlePDPItemLinkClick(matchingItem.sku),
                        { disabled: !matchingItem?.sku },
                      )}
                      role='button'
                      tabIndex={matchingItem?.sku ? 0 : -1}
                    >
                      {marketingItem?.image?.url && (
                        <S.ItemImage $isSixItemLayout={isSixItemLayout}>
                          <Image
                            alt={
                              marketingItem.image.title ||
                              marketingItem.text ||
                              ''
                            }
                            height={
                              isSixItemLayout ? (isMobile ? 120 : 50) : 70
                            }
                            sizes='(max-width: 768px) 100px, 30px'
                            src={marketingItem.image.url}
                            style={{
                              objectFit: 'contain',
                              maxWidth: '100%',
                              maxHeight: '100%',
                            }}
                            width={isSixItemLayout ? (isMobile ? 120 : 50) : 70}
                          />
                        </S.ItemImage>
                      )}
                      <S.ItemInfo>
                        <S.ItemDetails
                          $isSixItemLayout={isSixItemLayout}
                          data-test-id={`marketingSelectorModalOption${originalIndex}-marketingItemName`}
                        >
                          {marketingItem?.text || ''}
                        </S.ItemDetails>
                      </S.ItemInfo>
                    </S.OptionBox>
                  );
                } else {
                  return (
                    <S.OptionBox
                      key={marketingItem?.reference || originalIndex}
                      aria-disabled={!marketingItem?.slug}
                      aria-label={marketingItem?.text ?? ''}
                      className='marketing-item-selector'
                      data-test-id={`marketingSelectorModalOption-${originalIndex}`}
                      onClick={() =>
                        handlePLPItemLinkClick(marketingItem?.slug)
                      }
                      onKeyDown={createKeyboardClickHandler(
                        () => handlePLPItemLinkClick(marketingItem?.slug),
                        { disabled: !marketingItem?.slug },
                      )}
                      role='button'
                      tabIndex={marketingItem?.slug ? 0 : -1}
                    >
                      {marketingItem?.image?.url && (
                        <S.ItemImage $isSixItemLayout={isSixItemLayout}>
                          <Image
                            alt={
                              marketingItem.image.title ||
                              marketingItem.text ||
                              ''
                            }
                            height={
                              isSixItemLayout ? (isMobile ? 120 : 50) : 70
                            }
                            sizes='(max-width: 768px) 100px, 30px'
                            src={marketingItem.image.url}
                            style={{
                              objectFit: 'contain',
                              maxWidth: '100%',
                              maxHeight: '100%',
                            }}
                            width={isSixItemLayout ? (isMobile ? 120 : 50) : 70}
                          />
                        </S.ItemImage>
                      )}
                      <S.ItemInfo>
                        <S.ItemDetails
                          $isSixItemLayout={isSixItemLayout}
                          data-test-id={`marketingSelectorModalOption${originalIndex}-marketingItemName`}
                        >
                          {marketingItem?.text || ''}
                        </S.ItemDetails>
                      </S.ItemInfo>
                    </S.OptionBox>
                  );
                }
              });
            })()}
        </S.OptionsContainer>
      </Modal.Body>
    </Modal>
  );
};

export default MarketingSelectorModal;
